import {
	BackdropCircularProgress,
	EnhancedDropDownRHFWithLabel,
	EnhancedIOSSwitchRHFWithLabel,
	EnhancedSnackbar,
	IEnhancedDropDownItem,
	IResponse,
	IServerError,
	ITest,
	LabelPosition,
	RHookFormMaskedTextFieldWithLabel,
	RHookFormTextFieldWithLabel,
	StandardPanel,
	StandardSubmitFormButtonsToolbar,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	RL1_SETTINGS_SCREEN_SLUG,
} from "../../../common/constants";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BasicSetupFormToolbar } from "../../BasicSetupFormToolbar";
import { FieldWrapper } from "../../FieldWrapper/FieldWrapper";
import { Grid } from "@mui/material";
import { IAppState } from "../../../store/reducers";
import { IGetListRequest } from "../../../models";
import { RL1SlipService } from "../../../services/RL1SlipService";
import { RL1SlipSettingValidation } from "./RL1SettingValidation";
import { T4SlipService } from "../../../services/T4SlipService";
import { YearService } from "../../../services/YearService";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { scrollPanelFieldsToTop, useNavigateToLogout } from "../../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IRL1SlipSettingsSetup extends ITest { }

const DEFAULT_VALUES = {};
const useYears = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["years-list", params],
		queryFn: (data) => {
			return YearService.getList(params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetTransmitterDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["transmitters"],
		queryFn: () => {
			return T4SlipService.getAllActiveTransmittersByClientIdAndTaxYear(
				clientId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});
const RL1_SETTING_DATA_QUERY_KEY = "get-rl1-setting-data";
const useGetRL1SettingData = (clientid: string, taxYear: number) =>
	useQuery({
		queryKey: [RL1_SETTING_DATA_QUERY_KEY, clientid, taxYear],
		queryFn: (data) => {
			return RL1SlipService.getRL1SettingData(clientid, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const RL1SlipSettingsSetup: FC<IRL1SlipSettingsSetup> = ({ testId }) => {
	const dispatch = useDispatch();
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const isPendingActivityLogData = useSelector(
		(state: IAppState) => state.isLoadAxiosCall.isLoading
	);

	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isPendingData, setIsPendingData] = useState(false);
	// const queryClient = useQueryClient();

	//new code
	const id = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const [selectedYear, setSelectedYear] = useState(taxYear);

	const { isPending: isSubmitting, mutate: submitRL1SettingData } = useMutation(
		{
			mutationFn: RL1SlipService.rl1SettingCreate,

			onSuccess: (res: any | IServerError | IResponse) => {
				// if (instanceOfIFlexFieldSetupRequest(res)) {
				// // remove the cache of the flex fields since we just added a new field.
				// queryClient.invalidateQueries(LIST_FIELDS_QUERY_KEY);
				// // [corner case] remove the cache of the field that has just been edited in-case the user re-selects it again
				// queryClient.invalidateQueries([FLEX_FIELD_DATA_QUERY_KEY, res.id]);
				// // set the currently added/edited flex field as the selected field
				// formMethods.setValue("id", res.id);
				if (res.message) {
					setSnackbarData({
						open: true,
						message: res.message,
						severity: "error",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
				}
			},
			onError: (err) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		}
	);
	// const { isPending: isPendingFlexFieldsList, data: flexFieldsList } =
	//   useGetFlexFieldsList();
	// const validationSchema = useMemo(
	//   () =>
	//     getValidationSchema(
	//     //   flexFieldsList && Array.isArray(flexFieldsList)
	//     //     ? flexFieldsList.map((flexField: any) => ({
	//     //         id: flexField.id,
	//     //         value: flexField.fieldId,
	//     //       }))
	//     //     : []
	//     ),
	//   [flexFieldsList]
	// );
	const formMethods = useForm<any>({
		resolver: yupResolver(RL1SlipSettingValidation),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});
	// const [selectedFlexFieldId, selectedParent] = useWatch({
	//   control: formMethods.control,
	//   name: ["id", "parent"],
	// });
	// const { isPending: isPendingSelectedFlexField, data: selectedFlexFieldData } =
	//   useGetFlexFieldData(selectedFlexFieldId as number);

	/**
	 * Handle when the form is submitted.
	 *
	 * @param {IFlexFieldSetupRequest} formData
	 */
	const onSubmit = (formData: any) => {
		const requestPayload = {
			id: id,
			taxYear: formData.taxYear,
			transmitter: formData.transmitter,
			copyFromPreviousYear: formData.copyFromPreviousYear,
			submissionMethod: parseInt(formData.submissionMethod),
			sourceOfRl1SlipsUsed: formData.sourceOfRl1SlipsUsed,
			sequentialNumberStart: formData.sequentialNumberStart,
			sequentialNumberEnd: formData.sequentialNumberEnd,
			quebecEnterprise:
				formData.quebecEnterprise !== ""
					? formData.quebecEnterprise
					: undefined,
			identification:
				formData.identification &&
				formData.identification.replace(/(\d{10})(\d{4})/, "$1RS$2"),
		};
		submitRL1SettingData(requestPayload);

	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
	};

	/**
	 * On Snackbar closed.
	 *
	 * @param {React.SyntheticEvent | Event} event
	 * @param {string} reason
	 */
	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	// const yearListItems = useMemo(() => {
	//   return [
	//     {
	//       label: "2020",
	//       value: "2020",
	//     },
	//     {
	//       label: "2021",
	//       value: "2021",
	//     },
	//   ];
	//   // if (!flexFieldsList) {
	//   //   return [];
	//   // }
	//   // return flexFieldsList.map((flexField) => ({
	//   //   label: flexField.fieldLabel,
	//   //   value: flexField.id,
	//   // }));
	// }, []);

	// set the values of the selected flex field coming from BE.
	// useEffect(() => {
	//   // if the selected flex field data has loaded.
	//   if (selectedFlexFieldData) {
	//     Object.keys(selectedFlexFieldData).forEach((fieldName) => {
	//       // @ts-ignore
	//       const fieldValue = selectedFlexFieldData[fieldName];
	//       // @ts-ignore
	//       formMethods.setValue(fieldName, fieldValue);
	//     });
	//   }
	// }, [selectedFlexFieldData, formMethods.setValue, formMethods]);

	// if we have selected the parent and a flex field and they both are the same, we should un-set the parent because a field cannot be the parent of itself
	// useEffect(() => {
	//   if (
	//     selectedFlexFieldId &&
	//     selectedParent &&
	//     selectedParent === selectedFlexFieldId
	//   ) {
	//     formMethods.setValue("parent", undefined);
	//   }
	// }, [formMethods, selectedFlexFieldId, selectedParent]);

	//new code
	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "role_name",
		order_direction: "asc",
		page: 1,
		size: INITIAL_PAGE_SIZE,
	});
	const navigateToLogout = useNavigateToLogout();
	const { isPending, data } = useYears(queryParams, navigateToLogout); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const YearsList: any = data;
	console.log("yyyyyyyyyyy", YearsList)

	const years = YearsList?.items.map((x: any) => {
		const { taxYear, id, ...rest } = x; // Extract year property and the rest of the properties
		return { ...rest, value: taxYear, label: taxYear }; // Rename year to value
	});

	console.log("yyyyyyyyyyy", years)
	const { isPending: isPendingTransmitterDDL, data: transmitterDDL } =
		useGetTransmitterDDL(id, taxYear);


	const taxYearValue = formMethods.getValues("taxYear");
	const {
		isPending: isPendingSummaryGetData,
		data: rl1SettingData,
		refetch: refetchRL1SettingData,
	} = useGetRL1SettingData(id, selectedYear);



	//shown geted Data
	useEffect(() => {

		// if the selected flex field data has loaded.
		if (rl1SettingData && rl1SettingData.id) {
			Object.keys(rl1SettingData).forEach((fieldName) => {
				// @ts-ignore
				const fieldValue = rl1SettingData[fieldName];


				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "quebecEnterprise") {
					formMethods.setValue(fieldName, fieldValue.toString());
				}
			});
		} else if (rl1SettingData && rl1SettingData.message) {
			setSnackbarData({
				open: true,
				message: rl1SettingData.message.message
					? rl1SettingData.message.message
					: rl1SettingData.message
						? rl1SettingData.message
						: "Something went wrong!",
				severity: "error",
			});
			formMethods.reset();
			formMethods.setValue("taxYear", selectedYear);
		} else {
			formMethods.reset();
		}
	}, [rl1SettingData, formMethods.setValue, formMethods]);


	const onYearSelectionChangeHandler = (item: IEnhancedDropDownItem) => {
		setSelectedYear(Number(item.value));
		refetchRL1SettingData();
	};

	const onCopyFromPrevoisYearChangedHandler = (value: boolean) => {
		if (value) {
			(async () => {
				try {
					setIsPendingData(true);
					const response: any = await RL1SlipService.getRL1SettingData(
						id,
						selectedYear - 1
					);

					if (response) {
						if (response && response.id) {
							Object.keys(response).forEach((fieldName) => {
								const fieldValue = response[fieldName];
								if (fieldName === "sequentialNumberStart") {
									formMethods.setValue(fieldName, fieldValue ?? undefined);
								}
								if (fieldName === "sequentialNumberEnd") {
									formMethods.setValue(fieldName, fieldValue ?? undefined);
								}
							});
						} else if (response && response.message) {
							setSnackbarData({
								open: true,
								message: response.message.message
									? response.message.message
									: response.message
										? response.message
										: "Something went wrong!",
								severity: "error",
							});
							formMethods.setValue("sequentialNumberStart", undefined);
							formMethods.setValue("sequentialNumberEnd", undefined);
						}
					}
				} catch (error) {
					setIsPendingData(false);
					console.error("Error fetching data:", error);
				} finally {
					setIsPendingData(false);
				}
			})();
		} else {
			formMethods.setValue("sequentialNumberStart", undefined);
			formMethods.setValue("sequentialNumberEnd", undefined);
		}
	};

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Compliance", "RL-1"];
	const expandedRL1SettingItem = "Settings";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "RL-1" && item.label === expandedRL1SettingItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedRL1SettingItem && "/compliance/rl1/settings",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: RL1_SETTINGS_SCREEN_SLUG,
				companyName: clientName,
				activityType: "activity_type=create&activity_type=updated",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);
	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
		// sx={{ "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={
					isPending ||
					// isPendingSelectedFlexField ||
					isSubmitting ||
					// isPendingFlexFieldsList ||
					isPendingTransmitterDDL ||
					isPendingSummaryGetData ||
					isPendingActivityLogData ||
					isPendingData
				}
				testId={testId}
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid
							size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
							sx={{
								borderRadius: "3px",
							}}
						>
							<TwoColumnLayout
								toolbar={
									<BasicSetupFormToolbar
										onResetClicked={onResetClicked}
										isSubmitting={isSubmitting}
										pageTitle={"RL-1 Settings"}
									/>
								}
								leftColumnContent={
									<>
										<StandardPanel title="Year" testId={`${testId}-year-panel`}
											borderHide={true}
											borderBottom={true}>
											<EnhancedDropDownRHFWithLabel
												label="Select Year"
												selectName="taxYear"
												items={years ?? []}
												testId={`${testId}-selected-year`}
												labelPosition={LabelPosition.Left}
												selectedItem={selectedYear}
												onSelectionChangeHandler={onYearSelectionChangeHandler}
											/>
										</StandardPanel>
										<StandardPanel
											title="Slip Data"
											testId={`${testId}-slip-data`}
											borderHide={true}
										>
											<EnhancedDropDownRHFWithLabel
												label="Transmitter"
												selectName="transmitter"
												items={
													transmitterDDL && !transmitterDDL.message
														? transmitterDDL
														: []
												}
												// selectedItem="2020"
												testId={`${testId}-transmitter`}
												labelPosition={LabelPosition.Top}
												required
											/>
											<EnhancedDropDownRHFWithLabel
												label="Submission Method"
												selectName="submissionMethod"
												items={[
													{ label: "XML", value: 1 },
													{ label: "Paper", value: 2 },
												]}
												selectedItem={1}
												testId={`${testId}-submission-method`}
												labelPosition={LabelPosition.Top}
												required
											/>
											<EnhancedDropDownRHFWithLabel
												label="Source of RL-1 Slips Used"
												selectName="sourceOfRl1SlipsUsed"
												items={[
													{
														label:
															"if the RL slips were obtained from Revenu Québec",
														value: "if the RL1 slips were obtained from quebec",
													},
													{
														label:
															"if the RL slips are facsimiles obtained from a third party",
														value:
															"if the RL1 slips are facsimiles obtained from a third party",
													},
													{
														label:
															"if the RL slips are facsimiles produced by the transmitter",
														value:
															"if the RL1 slips are facsimiles produced by the transmitter",
													},
													{
														label:
															"if the source of the RL slips is any combination of the above",
														value:
															"if the source of the RL1 slips is any combination of the above",
													},
												]}
												// selectedItem="1"
												testId={`${testId}-source-of-rl-slips-used`}
												labelPosition={LabelPosition.Top}
												required
											/>
										</StandardPanel>
									</>
								}
								rightColumnContent={
									<>
										<StandardPanel
											title="Sequential Numbers"
											testId={`${testId}-sequential-numbers`}
											borderHide={true}
											borderBottom={true}
										>
											<FieldWrapper>
												<EnhancedIOSSwitchRHFWithLabel
													name="copyFromPreviousYear"
													checked={false}
													testId="copyFromPreviousYear"
													label="Copy from Previous Year"
													labelPosition={LabelPosition.Top}
													onSelectionChangeHandler={
														onCopyFromPrevoisYearChangedHandler
													}
												/>
											</FieldWrapper>
											{/* <RHookFormTextFieldWithLabel
                    label="Start #"
                    id="sequentialNumberStart"
                    testId={`${testId}-start-number`}
                    labelPosition={LabelPosition.Top}
                    required
                  /> */}
											{/* <RHookFormTextFieldWithLabel
                    label="End #"
                    id="sequentialNumberEnd"
                    testId={`${testId}-end-number`}
                    labelPosition={LabelPosition.Top}
                    required
                  /> */}
											<RHookFormMaskedTextFieldWithLabel
												label="Start #"
												id="sequentialNumberStart"
												testId={`${testId}-start-number`}
												labelPosition={LabelPosition.Top}
												placeholder={"00000000"}
												required
												inputMaskPattern={"00000000"}
												labelColumns={4}
												fieldColumns={8}
											/>
											<RHookFormMaskedTextFieldWithLabel
												label="End #"
												id="sequentialNumberEnd"
												testId={`${testId}-end-number`}
												labelPosition={LabelPosition.Top}
												placeholder={"00000000"}
												required
												inputMaskPattern={"00000000"}
												labelColumns={4}
												fieldColumns={8}
											/>
										</StandardPanel>
										<StandardPanel
											title="Enterprise/Identification Number"
											testId={`${testId}-enterprise-identification-number-panel`}
											borderHide={true}
										>
											{/* <RHookFormTextFieldWithLabel
                    label="Quebec Enterprise #"
                    id="quebecEnterprise"
                    testId={`${testId}-start-number`}
                    labelPosition={LabelPosition.Top}
                  /> */}
											<RHookFormMaskedTextFieldWithLabel
												label="Quebec Enterprise #"
												id="quebecEnterprise"
												testId={`${testId}-quebecEnterprise`}
												labelPosition={LabelPosition.Top}
												placeholder={"0000000000"}
												required
												inputMaskPattern={"0000000000"}
												labelColumns={4}
												fieldColumns={8}
											/>
											<RHookFormMaskedTextFieldWithLabel
												id="identification"
												label="Identification #"
												required
												testId={`${testId}-identification-number`}
												labelPosition={LabelPosition.Top}
												placeholder={"0000000000RS0000"}
												inputMaskPattern={"0000000000RS0000"}
											/>
										</StandardPanel>
									</>
								}
								testId="rl1-settings-setup-form"
							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`${testId}-snackbar`}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
